import EventEmitter from 'eventemitter3';

class MainSmoother {
  constructor() {
    this._rafID = null;

    this.update = this.update.bind(this);
    this.add = this.add.bind(this);
    this.remove = this.remove.bind(this);
    this.cancel = this.cancel.bind(this);
    this.pool = [];
  }

  add(options) {
    const instance = new Smooth(options);
    instance.smoother = this;
    this.pool.push(instance);
    return instance;
  }

  remove(instance) {
    const index = this.pool.indexOf(instance);

    if (index > -1) {
      this.pool.splice(index, 1);
    }
  }

  update() {
    let hasUpdated = false;

    for (const instance of this.pool) {
      if (instance.update()) {
        hasUpdated = true;
      }
    }

    if (hasUpdated) {
      this._rafID = requestAnimationFrame(this.update);
    } else {
      this.cancel();
    }
  }

  cancel() {
    cancelAnimationFrame(this._rafID);
    this._rafID = null;
  }

  get updating() {
    return this._rafID !== null;
  }
}

export const Smoother = new MainSmoother();

const defaultOptions = {
  smooth: 0.1,
  default: 0,
  precision: 2,
  min: -1,
  max: -1,
  resistance: false,
};

class Smooth extends EventEmitter {
  constructor(options) {
    super();

    this.options = Object.assign({}, defaultOptions, options);

    this.update = this.update.bind(this);
    this.compute = this.compute.bind(this);
    // this.destroy = this.destroy.bind(this);

    this._rafID = null;
    this._raw = this.options.default;
    this._value = this.options.default;
    this._target = this.options.default;
    this._precision = Math.pow(10, this.options.precision);
  }

  compute(value, target) {
    const { resistance, min, max, smooth } = this.options;

    if (resistance !== false) {
      if (target > max) {
        target = max + Math.sqrt(Math.abs(target - max) * (1 / resistance));
      }

      if (target < min) {
        target = min - Math.sqrt(Math.abs(target - min) * (1 / resistance));
      }
    }

    value += (target - value) * smooth;

    return value;
  }

  update() {
    this._raw = this.compute(this._raw, this._target);

    const value = Math.round(this._raw * this._precision) / this._precision;

    if (this.value !== this._target) {
      this.value = value;
      return true;
    } else {
      if (this._endValue !== value) {
        this.emit('end', value);
      }
      return false;
    }
  }

  set(value) {
    this._raw = value;
    this._target = value;
    this._value = value;
  }

  setMin(min) {
    this.options.min = min;
    this.target = Math.max(this.target, min);
  }

  get value() {
    return this._value;
  }

  set value(value) {
    if (this._value === value) {
      return value;
    }

    this._value = value;
    this.emit('update', this._value);
  }

  get target() {
    return this._target;
  }

  set target(target) {
    if (target === this._target) {
      return target;
    }

    if (this.options.resistance === false) {
      this._target = Math.max(this.options.min, Math.min(this.options.max, target));
    } else {
      this._target = target;
    }

    if (this.smoother.updating === false) {
      this.smoother.update();
    }
  }
}

export default Smooth;
